'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import accessClient from './accessClient'

interface SessionContextType {
  hasAccess: boolean
  isLoading: boolean
  hydrate: () => Promise<boolean>
  logout: () => Promise<void>
  /** Called by Auth bridge wiring (Task 2) when parent grants session → isAuthenticated */
  onAccessGranted?: (granted: boolean) => void
}

const SessionContext = createContext<SessionContextType | undefined>(undefined)

type SessionProviderProps = {
  children: React.ReactNode
  /** Additive AuthContext bridge — set isAuthenticated when server session is valid */
  onAccessGranted?: (granted: boolean) => void
}

export function SessionProvider({
  children,
  onAccessGranted,
}: SessionProviderProps) {
  const [hasAccess, setHasAccess] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const hydrate = useCallback(async () => {
    try {
      const session = await accessClient.getSession()
      setHasAccess(session.hasAccess)
      if (session.hasAccess) {
        onAccessGranted?.(true)
      }
      return session.hasAccess
    } catch {
      setHasAccess(false)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [onAccessGranted])

  useEffect(() => {
    void hydrate()
  }, [hydrate])

  const logout = useCallback(async () => {
    await accessClient.logout()
    setHasAccess(false)
    onAccessGranted?.(false)
  }, [onAccessGranted])

  return (
    <SessionContext.Provider
      value={{ hasAccess, isLoading, hydrate, logout, onAccessGranted }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  const context = useContext(SessionContext)
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}
